// File: src/pages/medisense/TalkingLabel.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faExpand,
  faCompress,
  faVolumeUp,
  faStop,
} from "@fortawesome/free-solid-svg-icons";

const tabs = [
  { id: "labels", label: "🏷️ About Labels" },
  { id: "tala", label: "🔊 About Talking Labels" },
  { id: "scanlabel", label: "📷 Scan Label" },
  { id: "myscanlog", label: "🧾 My Scan Log" },
  { id: "scanlog", label: "🧾 Scan Log" },
  { id: "reprotexpiry", label: "⏰ Report Expiry" },
  { id: "reportsuspect", label: "🚨 Report Suspect" },
  { id: "companies", label: "🏭 Pharma Companies" },
  { id: "doctors", label: "🩺 Doctors" },
  { id: "sales", label: "🤝 Supply Chain" },
  { id: "manufactureres", label: "🏷️ Label Manufactureres" },
];


export default function TalkingLabel() {
  const [activeTab, setActiveTab] = useState("labels");
  const [zoom, setZoom] = useState(1);
  const [activeTTS, setActiveTTS] = useState(null);
  const [languageMap, setLanguageMap] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Mock scan data
  const mockData = [
    { id: 1, medicine: "Paracetamol 500mg", datetime: "17/01/2026 09:01 AM", batch: "BATCH001", status: "checked" },
    { id: 2, medicine: "Paracetamol 500mg", datetime: "17/01/2026 09:02 AM", batch: "BATCH002", status: "pending" },
    { id: 3, medicine: "Ibuprofen 200mg", datetime: "17/01/2026 09:03 AM", batch: "BATCH003", status: "checked" },
    { id: 4, medicine: "Paracetamol 500mg", datetime: "17/01/2026 09:04 AM", batch: "BATCH004", status: "pending" },
    { id: 5, medicine: "Ibuprofen 200mg", datetime: "17/01/2026 09:05 AM", batch: "BATCH005", status: "checked" },
  ];

   // Filtered list
  const filteredData = mockData.filter((item) => {
    const matchesSearch = item.medicine.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "" || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Summary counts
  const totalScans = filteredData.length;
  const totalChecked = filteredData.filter((i) => i.status === "checked").length;
  const totalPending = filteredData.filter((i) => i.status === "pending").length;

 // Supported languages
  const languages = [
    { label: "English", code: "en-US" },
    { label: "Hindi", code: "hi-IN" },
    { label: "Japanese", code: "ja-JP" },
  ];

  const mockLabel = {
    name: "Paracetamol 500mg",
    manufactured: "01/01/2026",
    expiry: "01/01/2028",
    mrp: "₹50",
    manufacturer: "Think5Whys Pharma Pvt. Ltd.",
    productSpec: "Tablet, Pain Relief, Fever Reducer",
    specialInfo: "Store below 25°C. Keep out of reach of children.",
    instructions: "Take 1 tablet every 8 hours after food.",
  };

  // TTS functions
  const speakText = (text, idx, langCode = "en-US") => {
    if ("speechSynthesis" in window) {
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = langCode;
      window.speechSynthesis.cancel(); // stop any previous speech
      window.speechSynthesis.speak(utter);
      setActiveTTS(idx);
      utter.onend = () => setActiveTTS(null);
    } else alert("TTS not supported in this browser.");
  };

  const stopSpeech = () => {
    window.speechSynthesis.cancel();
    setActiveTTS(null);
  };

  // Language change per section
  const handleLanguageChange = (idx, langCode) => {
    setLanguageMap((prev) => ({ ...prev, [idx]: langCode }));
  };

  // Zoom handlers
  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.2, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.2, 0.5));

  // Calculate expiry
  const today = new Date();
  const expiryDate = new Date(mockLabel.expiry);
  const timeDiff = expiryDate - today;
  const expired = timeDiff < 0;
  const daysToExpire = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

  // Mock label with translations
  const sections = [
    {
      title: "1️⃣ Name & Dates",
      content: {
        "en-US": "Name: Paracetamol 500mg\nManufactured: 01/01/2026\nExpiry: 01/01/2028\nMRP: ₹50",
        "hi-IN": "नाम: पैरासिटामोल 500मि.ग्रा.\nनिर्माण: 01/01/2026\nसमाप्ति: 01/01/2028\nमूल्य: ₹50",
        "ja-JP": "名前: パラセタモール500mg\n製造日: 01/01/2026\n有効期限: 01/01/2028\n価格: ₹50",
      },
    },
    {
      title: "2️⃣ Manufacturer Details",
      content: {
        "en-US": "Name: Paracetamol 500mg\nManufacturer: Think5Whys Pharma Pvt. Ltd.",
        "hi-IN": "नाम: पैरासिटामोल 500मि.ग्रा.\nनिर्माता: Think5Whys Pharma Pvt. Ltd.",
        "ja-JP": "名前: パラセタモール500mg\n製造元: Think5Whys Pharma Pvt. Ltd.",
      },
    },
    {
      title: "3️⃣ Product Specification",
      content: {
        "en-US": "Name: Paracetamol 500mg\nSpecification: Tablet, Pain Relief, Fever Reducer",
        "hi-IN": "नाम: पैरासिटामोल 500मि.ग्रा.\nविशेष विवरण: टैबलेट, दर्द निवारक, बुखार कम करने वाली",
        "ja-JP": "名前: パラセタモール500mg\n仕様: 錠剤、鎮痛、解熱剤",
      },
    },
    {
      title: "4️⃣ Special Info & Instructions",
      content: {
        "en-US": "Name: Paracetamol 500mg\nSpecial Info: Store below 25°C. Keep out of reach of children.\nInstructions: Take 1 tablet every 8 hours after food.",
        "hi-IN": "नाम: पैरासिटामोल 500मि.ग्रा.\nविशेष जानकारी: 25°C से नीचे रखें। बच्चों की पहुंच से दूर रखें।\nनिर्देश: भोजन के बाद हर 8 घंटे में 1 टैबलेट लें।",
        "ja-JP": "名前: パラセタモール500mg\n特別情報: 25°C以下で保存。子供の手の届かないところに保管。\n指示: 食後8時間ごとに1錠服用。",
      },
    },
    {
      title: "5️⃣ Complete Label Info",
      content: {
        "en-US": `Name: Paracetamol 500mg
        Manufactured: 01/01/2026
        Expiry: 01/01/2028
        MRP: ₹50
        Manufacturer: Think5Whys Pharma Pvt. Ltd.
        Specification: Tablet, Pain Relief, Fever Reducer
        Special Info: Store below 25°C. Keep out of reach of children.
        Instructions: Take 1 tablet every 8 hours after food.`,
                "hi-IN": `नाम: पैरासिटामोल 500मि.ग्रा.
        निर्माण: 01/01/2026
        समाप्ति: 01/01/2028
        मूल्य: ₹50
        निर्माता: Think5Whys Pharma Pvt. Ltd.
        विशेष विवरण: टैबलेट, दर्द निवारक, बुखार कम करने वाली
        विशेष जानकारी: 25°C से नीचे रखें। बच्चों की पहुंच से दूर रखें।
        निर्देश: भोजन के बाद हर 8 घंटे में 1 टैबलेट लें।`,
                "ja-JP": `名前: パラセタモール500mg
        製造日: 01/01/2026
        有効期限: 01/01/2028
        価格: ₹50
        製造元: Think5Whys Pharma Pvt. Ltd.
        仕様: 錠剤、鎮痛、解熱剤
        特別情報: 25°C以下で保存。子供の手の届かないところに保管。
        指示: 食後8時間ごとに1錠服用。`,
      },
    },
  ];

  const [selectedManufacturer, setSelectedManufacturer] = useState(null);

  // Dummy Label Manufacturer Data
  const manufacturers = [
    {
      id: 1,
      name: "Think5Whys Labels Pvt. Ltd.",
      location: "Mumbai, India",
      rating: 4.5,
      active: true,
    },
    {
      id: 2,
      name: "Global Med Labels Inc.",
      location: "Tokyo, Japan",
      rating: 4.2,
      active: false,
    },
    {
      id: 3,
      name: "SafePack Pharma Labels",
      location: "Berlin, Germany",
      rating: 4.8,
      active: true,
    },
    {
      id: 4,
      name: "HealthMark Labels Co.",
      location: "New York, USA",
      rating: 4.0,
      active: true,
    },
    {
      id: 5,
      name: "LabelTech Solutions",
      location: "Delhi, India",
      rating: 4.3,
      active: false,
    },
    {
      id: 6,
      name: "MediPrint Labels",
      location: "Osaka, Japan",
      rating: 4.6,
      active: true,
    },
  ];

  // Optional: Filter active / inactive if needed
  const [mfgstatusFilter, setMfgstatusFilter] = useState("");

  const filteredManufacturers = manufacturers.filter((m) => {
    if (!mfgstatusFilter) return true;
    return mfgstatusFilter === "active" ? m.active : !m.active;
  });

  return (
    <div className="min-h-screen flex flex-col bg-black text-white [&_p]:text-white [&_h3]:text-yellow-200 [&_h4]:text-green-200 [&_ul]:text-white [&_li]:text-white">
      <header className="flex flex-col justify-start items-start border-b border-green-500 px-4 py-3 sticky top-0 bg-black z-50 shadow-md">
        <h1 className="text-xl lg:text-2xl font-extrabold text-green-400">MediSense - Talking Labels</h1>
        <p className="text-sm lg:text-base text-white">Accessible, Safe & Smart Medical Labeling Ecosystem</p>
      </header>

      <nav className="bg-black border-b border-green-500 sticky top-[64px] z-40 p-2">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 place-items-center">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full text-center text-xs lg:text-sm font-semibold px-2 py-2 rounded-md transition ${
                activeTab === tab.id
                  ? "bg-green-700 text-yellow-200 border-b-2 border-green-400"
                  : "bg-green-900 text-white hover:bg-green-700 hover:text-white"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      <main className="flex-1 p-4 space-y-6">
        {activeTab === "labels" && (
          <section className="space-y-6">
            <h2 className="text-xl font-bold text-center text-green-400">
              🏷️ Understanding Labels — More Than Just a Tag
            </h2>

            {/* What is a Label */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-2">
              <h3 className="font-semibold text-yellow-200">📌 What is a Label?</h3>
              <p>
                A label is a <strong>name, tag, or identifier</strong> used to recognize,
                describe, classify, or distinguish something from others.
              </p>
              <p className="text-sm text-gray-300">
                In simple terms, a label answers:
              </p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>What is this?</li>
                <li>Which category does it belong to?</li>
                <li>How should it be identified or handled?</li>
              </ul>
            </div>

            {/* Types of Labels */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-2">
              <h3 className="font-semibold text-yellow-200">🧩 Types of Labels</h3>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li><strong>Physical:</strong> Product labels, medicine labels, warning tags</li>
                <li><strong>Conceptual:</strong> Job titles, roles, categories</li>
                <li><strong>Technical:</strong> Data labels, UI labels, code variables</li>
                <li><strong>Social:</strong> Brand names, group identities</li>
              </ul>
            </div>

            {/* Significance */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-3">
              <h3 className="font-semibold text-yellow-200">🎯 Why Labels Matter</h3>

              <ul className="list-disc list-inside text-sm space-y-1">
                <li><strong>Identification:</strong> Quickly recognize something (e.g., medicine name)</li>
                <li><strong>Classification:</strong> Group similar items (Fragile, Perishable)</li>
                <li><strong>Communication:</strong> Convey critical info instantly (Vegan, ISO Certified)</li>
                <li><strong>Decision Making:</strong> Influence actions (High Risk, Discounted)</li>
                <li><strong>Control & Management:</strong> Enable tracking and operations</li>
                <li><strong>Compliance & Safety:</strong> Mandatory for law and standards</li>
                <li><strong>Branding & Perception:</strong> Shape trust and value perception</li>
              </ul>
            </div>

            {/* Business & MSME */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-2">
              <h3 className="font-semibold text-green-300">🏢 Labels in Business & MSMEs</h3>
              <p className="text-sm">
                In business, labels act as <strong>commercial identifiers</strong> that
                enable selling, pricing, tracking, and compliance.
              </p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Product: Brand, SKU, Batch, MRP</li>
                <li>Process: Raw Material, WIP, Finished Goods</li>
                <li>Customer: Retail, Wholesale, Enterprise</li>
                <li>People: Skilled, Supervisor, Operator</li>
              </ul>
              <p className="text-sm text-red-400 mt-2">
                🔑 MSME Insight: Poor labeling leads to confusion, leakage, rework, and disputes.
              </p>
            </div>

            {/* AI & Data */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-2">
              <h3 className="font-semibold text-green-300">🤖 Labels in AI, Data & Technology</h3>
              <p className="text-sm">
                Labels attach <strong>structured meaning</strong> to data so machines can learn.
              </p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Image: Defective / Non-defective</li>
                <li>Customer: High Risk / Low Risk</li>
                <li>Ticket: Urgent / Normal</li>
              </ul>
              <p className="text-sm text-yellow-300 mt-2">
                🔑 AI Truth: Data without labels is noise. Labels turn data into intelligence.
              </p>
            </div>

            {/* Manufacturing */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-2">
              <h3 className="font-semibold text-green-300">🏭 Labels in Manufacturing & Quality</h3>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Status: Accepted, Rejected, Under Inspection</li>
                <li>Machine: Running, Idle, Breakdown</li>
                <li>Quality: OK, Rework, Scrap</li>
              </ul>
              <p className="text-sm text-yellow-300 mt-2">
                🔑 Factory Rule: If it’s not labeled, it’s not controlled.
              </p>
            </div>

            {/* UI/UX */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-2">
              <h3 className="font-semibold text-green-300">🖥️ Labels in UI/UX & Digital Platforms</h3>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Buttons: Submit, Save, Approve</li>
                <li>Fields: Quantity, Rate, GST</li>
                <li>Status: Pending, Approved, Closed</li>
              </ul>
              <p className="text-sm text-yellow-300 mt-2">
                🔑 Product Insight: Bad labels cause more support tickets than bad code.
              </p>
            </div>

            {/* Compliance */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-2">
              <h3 className="font-semibold text-green-300">⚖️ Labels in Compliance & Governance</h3>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Confidential</li>
                <li>Hazardous</li>
                <li>ISO 27001 Certified</li>
                <li>For Export Only</li>
              </ul>
              <p className="text-sm text-red-400 mt-2">
                🔑 Compliance Reality: Wrong or missing labels can cause fines, bans, or shutdowns.
              </p>
            </div>

            {/* Psychology & Branding */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-3">
              <h3 className="font-semibold text-green-300">🧠 Psychology, Branding & Systems Thinking</h3>

              <p className="text-sm">
                Labels shape <strong>identity, behavior, perception, and scale</strong>.
              </p>

              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Psychology: People often become what they are labeled as</li>
                <li>Branding: Your label speaks before you do</li>
                <li>Systems: Labels connect humans ↔ systems ↔ decisions</li>
              </ul>

              <p className="text-sm text-green-400 mt-2">
                🔑 Final Truth: Smart labels enable order, intelligence, control, trust, and scale.
              </p>
            </div>
          </section>
        )}     

        {activeTab === "tala" && (
          <section className="space-y-6">
            <h2 className="text-xl font-bold text-center text-green-400">
              🔊 TaLa — Talking Labels
            </h2>
            <p className="text-center text-sm text-gray-300">
              Global QR-Based Medicine Accessibility & Safety Platform
            </p>

            {/* ===================== EXECUTIVE SUMMARY ===================== */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-2">
              <h3 className="font-semibold text-yellow-200">📌 Executive Summary</h3>
              <p className="text-sm">
                Medicines save lives, but <strong>poorly accessible information</strong> on
                medicine packaging continues to cause misuse, wastage, adverse drug events,
                and loss of trust worldwide.
              </p>
              <p className="text-sm">
                TaLa (Talking Labels) augments mandatory physical medicine labels with
                <strong> verified, multilingual, voice-enabled, and zoomable digital information</strong>
                using secure QR codes.
              </p>
              <p className="text-sm text-green-300">
                TaLa does not replace statutory labels. It acts as an
                <strong> Accessibility, Safety, and Trust Layer</strong>
                connecting pharma, regulators, healthcare providers, and users.
              </p>
            </div>

            {/* ===================== PROBLEM STATEMENT ===================== */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-3">
              <h3 className="font-semibold text-red-400">⚠️ Problem Statement</h3>

              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Expiry dates and batch numbers printed in extremely small fonts</li>
                <li>Language mismatch between packaging and end users</li>
                <li>Low literacy and semi-literacy populations</li>
                <li>Elderly and visually impaired users unable to read labels</li>
                <li>Expiry format confusion (MM/YY vs DD/MM/YY)</li>
                <li>Dosage and composition misunderstanding</li>
              </ul>

              <p className="text-sm text-red-300">
                This leads to adverse drug reactions, medicine wastage, hospitalizations,
                legal disputes, and erosion of trust.
              </p>

              <p className="text-sm font-semibold text-yellow-300">
                🔑 This is not a UI problem — it is a systemic public health safety gap.
              </p>
            </div>

            {/* ===================== NEED & SOLUTION ===================== */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-3">
              <h3 className="font-semibold text-yellow-200">🧩 Why Talking Labels Are Needed</h3>

              <p className="text-sm">
                Physical labels are legally required but <strong>not universally accessible</strong>.
                TaLa addresses this gap by enhancing comprehension, not altering content.
              </p>

              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Accessibility without regulatory conflict</li>
                <li>Voice for literacy and vision barriers</li>
                <li>Language choice for global usage</li>
                <li>Clear prioritization of expiry, dosage, and warnings</li>
              </ul>
            </div>

            {/* ===================== THINK5WHYS MEDISENSE SOLUTION ===================== */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-3">
              <h3 className="font-semibold text-green-300">
                🧠 Think5Whys MediSense Solution
              </h3>

              <p className="text-sm">
                Each medicine pack contains a <strong>secure QR code</strong>.
                When scanned:
              </p>

              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Displays verified information in large, adjustable fonts</li>
                <li>Reads critical information aloud using text-to-speech</li>
                <li>Allows user-selected language</li>
                <li>Highlights expiry, dosage, and warnings first</li>
                <li>Supports reminders and caregiver access (optional)</li>
              </ul>

              <p className="text-sm text-green-400">
                🔑 Physical labels remain authoritative. Digital labels enhance safety.
              </p>
            </div>

            {/* ===================== USER EXPERIENCE ===================== */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-3">
              <h3 className="font-semibold text-green-300">🧑‍🦽 Patient-Centric Experience</h3>

              <ol className="list-decimal list-inside text-sm space-y-1">
                <li>Scan QR code on medicine pack</li>
                <li>Select preferred language</li>
                <li>Adjust font size / contrast</li>
                <li>Enable voice narration</li>
                <li>View prioritized safety information</li>
              </ol>

              <p className="text-sm text-gray-300">
                Designed for elders, caregivers, low-literacy users, and accessibility needs.
              </p>
            </div>

            {/* ===================== INFORMATION ARCHITECTURE ===================== */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-3">
              <h3 className="font-semibold text-green-300">📚 Medical Information Architecture</h3>

              <ul className="list-disc list-inside text-sm space-y-1">
                <li><strong>Critical Safety Layer:</strong> Expiry, dosage, contraindications</li>
                <li><strong>Core Medical Layer:</strong> Composition, indication, side effects</li>
                <li><strong>Extended Layer:</strong> Storage, missed dose, interactions</li>
                <li><strong>Regulatory Layer:</strong> Batch, manufacturer, approvals</li>
              </ul>
            </div>

            {/* ===================== VOICE & LANGUAGE ===================== */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-3">
              <h3 className="font-semibold text-green-300">🗣️ Voice & Multilingual System</h3>

              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Calm, medically appropriate voice tone</li>
                <li>Emphasis on warnings and expiry</li>
                <li>Repeat-on-demand capability</li>
                <li>Modular language expansion</li>
              </ul>
            </div>

            {/* ===================== ETHICAL GAMIFICATION ===================== */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-3">
              <h3 className="font-semibold text-yellow-200">🎯 Ethical Gamification (Optional)</h3>

              <p className="text-sm">
                Gamification is used only to encourage <strong>safe behavior</strong>, not entertainment.
              </p>

              <ul className="list-disc list-inside text-sm space-y-1">
                <li>“Expiry Checked Before Use” badge</li>
                <li>Medication adherence streaks</li>
                <li>Caregiver responsibility recognition</li>
                <li>Reporting unclear or damaged labels</li>
              </ul>

              <p className="text-sm text-red-400">
                No ads. No addiction loops. No medical advice replacement.
              </p>
            </div>

            {/* ===================== REGULATORY POSITIONING ===================== */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-3">
              <h3 className="font-semibold text-green-300">⚖️ Regulatory & Compliance Alignment</h3>

              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Digital label is supplementary</li>
                <li>Physical label remains authoritative</li>
                <li>No content change without approval</li>
                <li>Aligned with drug labeling and accessibility laws</li>
              </ul>
            </div>

            {/* ===================== FINAL POSITIONING ===================== */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-2">
              <h3 className="font-semibold text-green-400">🌍 Final Positioning</h3>
              <p className="text-sm">
                TaLa is not a QR feature or a mobile app.
              </p>
              <p className="text-sm font-semibold text-yellow-300">
                It is a Global Medicine Accessibility, Safety, and Trust Infrastructure.
              </p>
              <p className="text-sm">
                The question is no longer whether this is useful —
                <strong>but how soon it should be implemented.</strong>
              </p>
            </div>
          </section>
        )}

        {activeTab === "scanlabel" && (
           <section className="space-y-6">
            <div className="bg-black text-white p-4 space-y-4">
              <h1 className="text-xl font-bold text-green-400 text-center mb-4">
                📷 Scan Label
              </h1>

              <div className="flex justify-center">
              <img
                  src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=MockQR"
                  alt="Dummy QR Code"
                  className="w-36 h-36 object-contain border-2 border-green-700 rounded-md"
                />                
                <button className="bg-green-700 hover:bg-green-600 text-white font-semibold w-64 px-4 py-2 rounded-md flex items-center gap-2">
                  <FontAwesomeIcon icon={faSearch} />
                  Scan QR Code (Mock)
                </button>
              </div>

              <div className="flex justify-center gap-4">
                <button
                  onClick={handleZoomIn}
                  className="bg-green-900 hover:bg-green-700 text-white w-32 px-3 py-1 rounded-md flex items-center gap-1"
                >
                  <FontAwesomeIcon icon={faExpand} /> Zoom In
                </button>
                <button
                  onClick={handleZoomOut}
                  className="bg-green-900 hover:bg-green-700 text-white w-32 px-3 py-1 rounded-md flex items-center gap-1"
                >
                  <FontAwesomeIcon icon={faCompress} /> Zoom Out
                </button>
              </div>

              <div className="space-y-4 overflow-x-auto">
                {sections.map((sec, idx) => {
                  const selectedLang = languageMap[idx] || "en-US";
                  const displayedText = sec.content[selectedLang];

                  return (
                    <div
                      key={idx}
                      className="bg-green-900 p-4 rounded-xl shadow-md text-sm lg:text-base"
                      style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}
                    >
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2 gap-2">
                        <h3 className="font-semibold text-yellow-200">{sec.title}</h3>

                        <div className="flex gap-2 flex-wrap">
                          {/* Language Dropdown */}
                          <select
                            value={selectedLang}
                            onChange={(e) => handleLanguageChange(idx, e.target.value)}
                            className="bg-green-700 text-white px-2 py-1 rounded-md"
                          >
                            {languages.map((lang) => (
                              <option key={lang.code} value={lang.code}>
                                {lang.label}
                              </option>
                            ))}
                          </select>

                          {/* Listen / Stop */}
                          {activeTTS !== idx ? (
                            <button
                              onClick={() => speakText(displayedText, idx, selectedLang)}
                              className="bg-green-700 hover:bg-green-600 text-white px-2 py-1 rounded-md flex items-center gap-1"
                            >
                              <FontAwesomeIcon icon={faVolumeUp} /> Listen
                            </button>
                          ) : (
                            <button
                              onClick={stopSpeech}
                              className="bg-red-700 hover:bg-red-600 text-white px-2 py-1 rounded-md flex items-center gap-1"
                            >
                              <FontAwesomeIcon icon={faStop} /> Stop
                            </button>
                          )}
                        </div>
                      </div>

                      <pre className="whitespace-pre-wrap">{displayedText}</pre>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {activeTab === "myscanlog" && (
          <section className="space-y-6">
            <div className="bg-black text-white p-4 space-y-4 min-h-screen">
              <h1 className="text-xl font-bold text-green-400 text-center mb-4">
                🧾 My Scan Log
              </h1>

              {/* Filter / Search */}
              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <input
                  type="text"
                  placeholder="Search Medicine"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 p-3 rounded-md bg-green-900 border border-green-700 text-white"
                />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="p-3 h-12 rounded-md bg-green-900 border border-green-700 text-white"
                >
                  <option value="">Status: All</option>
                  <option value="checked">Checked ✅</option>
                  <option value="pending">Pending ⚠️</option>
                </select>
              </div>

              {/* My Scan Log Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full text-left border border-green-700">
                  <thead className="bg-green-900">
                    <tr>
                      <th className="px-4 py-2 border-b border-green-700">#</th>
                      <th className="px-4 py-2 border-b border-green-700">Medicine</th>
                      <th className="px-4 py-2 border-b border-green-700">Date / Time</th>
                      <th className="px-4 py-2 border-b border-green-700">Batch</th>
                      <th className="px-4 py-2 border-b border-green-700">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((item, idx) => (
                      <tr key={item.id} className="hover:bg-green-800">
                        <td className="px-4 py-2 border-b border-green-700">{idx + 1}</td>
                        <td className="px-4 py-2 border-b border-green-700">{item.medicine}</td>
                        <td className="px-4 py-2 border-b border-green-700">{item.datetime}</td>
                        <td className="px-4 py-2 border-b border-green-700">{item.batch}</td>
                        <td className="px-4 py-2 border-b border-green-700">
                          {item.status === "checked" ? (
                            <span className="text-yellow-400 font-semibold">Checked ✅</span>
                          ) : (
                            <span className="text-red-400 font-semibold">Pending ⚠️</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {filteredData.length === 0 && (
                      <tr>
                        <td colSpan="5" className="text-center py-4 text-gray-300">
                          No scans found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Optional: Show summary */}
              <div className="mt-4 bg-green-900 p-3 rounded-md shadow-md">
                <p>Total Scans: {totalScans}</p>
                <p>Checked: {totalChecked}</p>
                <p>Pending: {totalPending}</p>
              </div>
            </div>
          </section>
        )}

        {activeTab === "scanlog" && (
          <section className="space-y-6">
            <div className="bg-black text-white p-4 space-y-4 min-h-screen">
              <h1 className="text-xl font-bold text-green-400 text-center mb-4">
                🧾 Scan Log
              </h1>

              {/* Mock Scan Log Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full text-left border border-green-700">
                  <thead className="bg-green-900">
                    <tr>
                      <th className="px-4 py-2 border-b border-green-700">#</th>
                      <th className="px-4 py-2 border-b border-green-700">Medicine</th>
                      <th className="px-4 py-2 border-b border-green-700">Scanned By</th>
                      <th className="px-4 py-2 border-b border-green-700">Date / Time</th>
                      <th className="px-4 py-2 border-b border-green-700">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1,2,3,4,5].map((i) => (
                      <tr key={i} className="hover:bg-green-800">
                        <td className="px-4 py-2 border-b border-green-700">{i}</td>
                        <td className="px-4 py-2 border-b border-green-700">Paracetamol 500mg</td>
                        <td className="px-4 py-2 border-b border-green-700">User {i}</td>
                        <td className="px-4 py-2 border-b border-green-700">17/01/2026 10:0{i} AM</td>
                        <td className="px-4 py-2 border-b border-green-700">
                          {i % 2 === 0 ? (
                            <span className="text-yellow-400 font-semibold">Checked</span>
                          ) : (
                            <span className="text-red-400 font-semibold">Pending</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {activeTab === "reprotexpiry" && (
          <section className="space-y-6">
            <div className="bg-black text-white p-4 space-y-4 min-h-screen">
              <h1 className="text-xl font-bold text-green-400 text-center mb-4">
                ⏰ Report Expiry
              </h1>

              {/* Mock Expiry Report Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {[1,2,3,4].map((i) => {
                  const expiryDate = new Date();
                  expiryDate.setDate(expiryDate.getDate() + i*30);
                  return (
                    <div key={i} className="bg-green-900 p-4 rounded-xl shadow-md">
                      <h3 className="font-semibold text-yellow-200 mb-1">Paracetamol 500mg</h3>
                      <p>Batch: BATCH00{i}</p>
                      <p>Expiry: {expiryDate.toLocaleDateString()}</p>
                      <p>Status: {i % 2 === 0 ? "Expiring Soon ⚠️" : "Valid ✅"}</p>
                      <button className="mt-2 bg-green-700 hover:bg-green-600 px-3 py-1 rounded-md text-white font-semibold">
                        Notify / Mark
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          </section>
        )}

        {activeTab === "reportsuspect" && (
          <section className="space-y-6">
            <div className="bg-black text-white p-4 space-y-4 min-h-screen">
              <h1 className="text-xl font-bold text-green-400 text-center mb-4">
                🚨 Report Suspect
              </h1>

              {/* Mock Suspect Report Form */}
              <div className="space-y-4 max-w-md mx-auto">
                <input
                  type="text"
                  placeholder="Medicine Name"
                  className="w-full p-3 rounded-md bg-green-900 border border-green-700 text-white"
                />
                <input
                  type="text"
                  placeholder="Batch Number"
                  className="w-full p-3 rounded-md bg-green-900 border border-green-700 text-white"
                />
                <textarea
                  placeholder="Describe the issue"
                  className="w-full p-3 rounded-md bg-green-900 border border-green-700 text-white h-24 resize-none"
                />
                <button className="w-full bg-red-700 hover:bg-red-600 text-white font-semibold px-4 py-2 rounded-md">
                  Submit Report
                </button>
              </div>

              {/* Mock Previous Reports */}
              <div className="mt-6">
                <h3 className="font-semibold text-yellow-200 mb-2">Previous Reports</h3>
                <ul className="space-y-2">
                  {[1,2,3].map((i) => (
                    <li key={i} className="bg-green-900 p-3 rounded-md shadow-md flex justify-between items-center">
                      <span>Paracetamol 500mg - BATCH00{i}</span>
                      <span className="text-red-400 font-semibold">Reported</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}

        {activeTab === "companies" && (
          <section className="space-y-6">
            <div className="bg-black text-white p-4 flex flex-col justify-center items-center space-y-6">
              <h1 className="text-xl font-bold text-green-400 text-center mb-2">
                🏭 Pharma Companies
              </h1>

              <div className="bg-green-900 p-6 rounded-xl shadow-md text-center max-w-md">
                <p className="text-white text-sm lg:text-base mb-4">
                  To become a <strong>registered Pharma Company</strong> with 
                  <strong> Think5Whys Global DeepTech Solutions</strong> and make your 
                  <strong> Talking Label </strong> available for other stakeholders:
                </p>

                <p className="text-yellow-300 font-semibold text-lg break-words">
                  contact@think5whys.com
                </p>

                <p className="text-white text-xs mt-4">
                  After registration, your products can be scanned by hospitals, doctors, caregivers, and patients worldwide.
                </p>
              </div>
            </div>
          </section>
        )}

        {activeTab === "doctors" && (
          <section className="space-y-6">
            <div className="bg-black text-white p-4 flex flex-col justify-center items-center space-y-6">
              <h1 className="text-xl font-bold text-green-400 text-center mb-2">
                🩺 Doctors
              </h1>

              <div className="bg-green-900 p-6 rounded-xl shadow-md text-center max-w-md">
                <p className="text-white text-sm lg:text-base mb-4">
                  To become a <strong>registered Doctor</strong> with 
                  <strong> Think5Whys Global DeepTech Solutions</strong> and access 
                  <strong> Talking Labels</strong> for your patients and caregivers:
                </p>

                <p className="text-yellow-300 font-semibold text-lg break-words">
                  contact@think5whys.com
                </p>

                <p className="text-white text-xs mt-4">
                  After registration, you can scan medicine labels, verify authenticity, and ensure safe usage for your patients worldwide.
                </p>
              </div>
            </div>
          </section>
        )}

        {activeTab === "sales" && (
          <section className="space-y-2">
            <div className="bg-black text-white p-4 flex flex-col justify-center items-center space-y-2">
              <h1 className="text-xl font-bold text-green-400 text-center mb-2">
                🤝 Supply Chain
              </h1>

              <div className="bg-green-900 p-2 rounded-xl shadow-md text-center max-w-md">
                <p className="text-white text-sm lg:text-base mb-4">
                  To become a <strong>registered Supply Chain Partner</strong> with 
                  <strong> Think5Whys Global DeepTech Solutions</strong> and enable your 
                  <strong> products & services</strong> to integrate with our Talking Labels ecosystem:
                </p>

                <p className="text-yellow-300 font-semibold text-lg break-words">
                  contact@think5whys.com
                </p>

                <p className="text-white text-xs mt-4">
                  After registration, you can ensure your distribution, service, and logistics processes are recognized, trackable, and connected with hospitals, doctors, and caregivers worldwide.
                </p>
              </div>
            </div>
          </section>
        )}


        {activeTab === "manufactureres" && (
          <section className="space-y-6">
            <div className="bg-black text-white p-2 min-h-screen space-y-4">
                  <h1 className="text-xl font-bold text-green-400 text-center mb-2">
                    🏷️ Label Manufacturers
                  </h1>

                  {/* Filter */}
                  <div className="flex gap-2 mb-4">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="p-2 h-11 rounded-md bg-green-900 border border-green-700 text-white"
                    >
                      <option value="">All</option>
                      <option value="active">Active ✅</option>
                      <option value="inactive">Inactive ❌</option>
                    </select>
                  </div>

                  {/* Manufacturer Grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 text-left">
                    {filteredManufacturers.map((man) => (
                      <div
                        key={man.id}
                        onClick={() => setSelectedManufacturer(man)}
                        className="border rounded-xl p-4 shadow-sm cursor-pointer hover:shadow-md transition"
                      >
                        <h3 className="text-sm lg:text-lg font-semibold text-yellow-200">
                          {man.name}
                        </h3>
                        <p className="text-xs lg:text-sm text-white">{man.location}</p>

                        <p className="text-xs lg:text-sm mt-1 text-white">⭐ {man.rating}</p>

                        <p
                          className={`text-xs lg:text-sm mt-1 ${
                            man.active ? "text-green-600" : "text-red-500"
                          }`}
                        >
                          {man.active ? "🟢 Active" : "🔴 Inactive"}
                        </p>

                        {/* Action buttons */}
                        <div className="mt-2 flex flex-row gap-1">
                          <button className="text-xs lg:text-sm bg-blue-500 text-yellow-200 w-8 lg:w-12 px-1">
                            ℹ️
                          </button>
                          <button className="text-xs lg:text-sm bg-blue-500 text-yellow-200 w-8 lg:w-12 px-1">
                            💬
                          </button>
                          <button className="text-xs lg:text-sm bg-blue-500 text-yellow-200 w-8 lg:w-12 px-1">
                            📞
                          </button>
                          <button className="text-xs lg:text-sm bg-blue-500 text-yellow-200 w-8 lg:w-12 px-1">
                            📍
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>                

                  {selectedManufacturer && (
                    <div className="mt-4 p-4 rounded-md bg-green-900 shadow-md">
                      <h2 className="font-semibold text-yellow-200">
                        Selected: {selectedManufacturer.name}
                      </h2>
                      <p>Location: {selectedManufacturer.location}</p>
                      <p>Rating: {selectedManufacturer.rating}</p>
                      <p>Status: {selectedManufacturer.active ? "🟢 Active" : "🔴 Inactive"}</p>
                    </div>
                  )}
                </div>
            </section>
          )}



      </main>

      <footer className="bg-black border-t border-green-500 text-white py-4 px-4">
        <div className="flex flex-col lg:flex-row justify-between items-center text-center">
          <p className="text-xs lg:text-sm">&copy; 2026 Think5Whys Global DeepTech Solutions.</p>
          <div className="flex text-white space-x-6 mt-3 text-xs lg:text-sm">
            <Link to="/about-us" className="hover:text-green-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-green-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-green-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-green-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
